<template>
    <div class="time-progress-item" :class="{ compact }">
        <div class="time-progress-head">
            <span v-if="label" class="time-progress-label">{{ label }}</span>
            <span v-else-if="compact" class="time-progress-label">{{ progressPercent }}%</span>
            <span v-if="spentLabel" class="time-progress-hours" v-show="safeValue > 0">Completed: {{ formatLoggedHours(value) }} {{ hourLabel(value) }}</span>
            <span v-else class="time-progress-hours">{{ formatLoggedHours(value) }} / {{ formatLoggedHours(total) }} hrs</span>
        </div>
        <div class="time-progress-track">
            <b-progress :max="barMax" height="0.55rem">
                <template v-if="isOverCapacity && safeEstimate > 0 && safeEstimate < safeTotal">
                    <b-progress-bar :value="safeEstimate" variant="success"></b-progress-bar>
                    <b-progress-bar :value="safeTotal - safeEstimate" variant="warning" class="overcapacity-bar"></b-progress-bar>
                    <b-progress-bar :value="overCapacityHours" variant="warning" class="overcapacity-bar"></b-progress-bar>
                </template>
                <template v-else-if="isOverEstimateFill">
                    <b-progress-bar :value="safeEstimate" variant="success"></b-progress-bar>
                    <b-progress-bar :value="estimateOverflowHours" variant="warning" class="overcapacity-bar"></b-progress-bar>
                </template>
                <template v-else>
                    <b-progress-bar v-if="barToDateRegular > 0" :value="barToDateRegular" :variant="toDateRegularVariant"></b-progress-bar>
                    <b-progress-bar v-if="barToDateOvertime > 0" :value="barToDateOvertime" variant="warning" class="overcapacity-bar"></b-progress-bar>
                    <b-progress-bar v-if="barFutureGap > 0" :value="barFutureGap" class="time-progress-future-gap"></b-progress-bar>
                    <b-progress-bar v-if="barFutureRegular > 0" :value="barFutureRegular" variant="info"></b-progress-bar>
                    <b-progress-bar v-if="barFutureOvertime > 0" :value="barFutureOvertime" variant="warning" class="overcapacity-bar"></b-progress-bar>
                </template>
            </b-progress>
            <div v-if="showOverCapacityMarker || showMarker"
                 class="time-progress-today"
                 :class="{ 'is-fulfilled': isOnPace && !hasOvertime, 'is-over': hasOvertime }"
                 :style="{ left: activeMarkerPercent + '%' }"
                 :title="activeMarkerTitle">
            </div>
            <div v-if="showEstimateMarker"
                 class="time-progress-today is-estimate"
                 :class="{ 'is-over': isOverEstimate }"
                 :style="{ left: estimateMarkerPercent + '%' }"
                 :title="estimateMarkerTitle">
            </div>
        </div>
        <div v-if="caption" class="time-progress-meta" :class="{ 'is-overtime': hasOvertime || isOverEstimate }">{{ caption }}</div>
    </div>
</template>

<script>
    import workTypeChartMixin from "../mixinWorkTypeChart";

    export default {
        mixins: [workTypeChartMixin],
        props: {
            label: {
                type: String,
                default: ""
            },
            value: {
                type: Number,
                default: 0
            },
            total: {
                type: Number,
                default: 0
            },
            period: {
                type: String,
                default: ""
            },
            periodDate: {
                default: null
            },
            markerDate: {
                default: null
            },
            periodStartDate: {
                default: null
            },
            dailyCapacity: {
                type: Number,
                default: 0
            },
            compact: {
                type: Boolean,
                default: false
            },
            toDateValue: {
                type: Number,
                default: null
            },
            futureValue: {
                type: Number,
                default: 0
            },
            estimateValue: {
                type: Number,
                default: null
            },
            isDone: {
                type: Boolean,
                default: false
            },
            spentLabel: {
                type: Boolean,
                default: false
            },
            isWeekendDay: {
                type: Boolean,
                default: false
            },
            isHoliday: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            safeValue() {
                return Number(this.value) || 0;
            },
            safeTotal() {
                return Number(this.total) || 0;
            },
            progressPercent() {
                if (this.safeTotal > 0) {
                    return Math.round((this.safeValue / this.safeTotal) * 100);
                }
                return this.safeValue > 0 ? 100 : 0;
            },
            toDateHours() {
                if (this.toDateValue == null)
                    return this.safeValue;
                return Number(this.toDateValue) || 0;
            },
            futureHours() {
                return Number(this.futureValue) || 0;
            },
            barToDateRegular() {
                const reference = this.expectedToDate > 0 ? this.expectedToDate : this.safeTotal;
                if (reference <= 0)
                    return this.toDateHours;
                return Math.min(this.toDateHours, reference);
            },
            barToDateOvertime() {
                const reference = this.expectedToDate > 0 ? this.expectedToDate : this.safeTotal;
                if (reference <= 0)
                    return 0;
                return Math.max(0, this.toDateHours - reference);
            },
            barFutureGap() {
                if (this.futureHours <= 0 || this.expectedToDate <= 0)
                    return 0;
                return Math.max(0, this.expectedToDate - this.toDateHours);
            },
            barFutureRegular() {
                if (this.futureHours <= 0)
                    return 0;
                const remainingCapacity = Math.max(0, this.safeTotal - this.toDateHours);
                return Math.min(this.futureHours, remainingCapacity);
            },
            barFutureOvertime() {
                if (this.futureHours <= 0)
                    return 0;
                const remainingCapacity = Math.max(0, this.safeTotal - this.toDateHours);
                return Math.max(0, this.futureHours - remainingCapacity);
            },
            toDateRegularVariant() {
                if (this.expectedToDate > 0) {
                    if (this.toDateHours >= this.expectedToDate)
                        return "success";
                    return this.chooseProgressStyle(this.toDateHours, this.expectedToDate);
                }
                if (this.safeTotal > 0) {
                    if (this.toDateHours >= this.safeTotal)
                        return "success";
                    return this.chooseProgressStyle(this.toDateHours, this.safeTotal);
                }
                return "secondary";
            },
            hasFutureHours() {
                return this.futureHours > 0;
            },
            futureGapHours() {
                return this.barFutureGap;
            },
            isOverCapacity() {
                return this.safeTotal > 0 && this.safeValue > this.safeTotal;
            },
            hasToDateOvertime() {
                const reference = this.expectedToDate > 0 ? this.expectedToDate : this.safeTotal;
                return reference > 0 && this.toDateHours > reference;
            },
            hasOvertime() {
                return this.isOverCapacity || this.hasToDateOvertime;
            },
            overCapacityHours() {
                if (this.isOverCapacity)
                    return this.safeValue - this.safeTotal;
                if (this.hasToDateOvertime) {
                    const reference = this.expectedToDate > 0 ? this.expectedToDate : this.safeTotal;
                    return this.toDateHours - reference;
                }
                return 0;
            },
            barMax() {
                return Math.max(this.safeTotal || 1, this.safeValue, this.toDateHours + this.futureHours);
            },
            today() {
                const now = new Date();
                return new Date(now.getFullYear(), now.getMonth(), now.getDate());
            },
            resolvedMarkerDate() {
                return this.toDate(this.markerDate) || this.today;
            },
            periodBounds() {
                const date = this.toDate(this.periodDate) || this.resolvedMarkerDate;
                if (!date)
                    return null;

                if (this.period === "month") {
                    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
                    const start = this.maxDate(monthStart, this.toDate(this.periodStartDate) || monthStart);
                    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                    return { start, end };
                }

                if (this.period === "week") {
                    const start = this.startOfWeek(date);
                    const end = new Date(start);
                    end.setDate(start.getDate() + 6);
                    return { start, end };
                }

                if (this.period === "sprint") {
                    const start = this.toDate(this.periodStartDate);
                    const end = this.toDate(this.periodDate);
                    if (!start || !end)
                        return null;
                    return { start, end };
                }

                return null;
            },
            elapsedWorkdays() {
                const bounds = this.periodBounds;
                if (!bounds)
                    return 0;

                const { start, end } = bounds;
                const marker = this.resolvedMarkerDate;
                if (marker < start || marker > end)
                    return 0;

                return this.countWorkdays(start, marker);
            },
            expectedToDate() {
                if (this.elapsedWorkdays <= 0)
                    return 0;
                const capacity = Number(this.dailyCapacity) || 0;
                if (capacity > 0)
                    return Math.min(this.safeTotal, this.elapsedWorkdays * capacity);
                const bounds = this.periodBounds;
                const totalWorkdays = bounds ? this.countWorkdays(bounds.start, bounds.end) : 0;
                if (totalWorkdays <= 0)
                    return 0;
                return this.safeTotal * (this.elapsedWorkdays / totalWorkdays);
            },
            showMarker() {
                return !this.isOverCapacity && this.expectedToDate > 0 && this.safeTotal > 0;
            },
            markerPercent() {
                const percent = this.barMax > 0 ? (this.expectedToDate / this.barMax) * 100 : 0;
                return Math.min(99.2, Math.max(0.8, percent));
            },
            showOverCapacityMarker() {
                return this.isOverCapacity || (this.expectedToDate <= 0 && this.hasToDateOvertime);
            },
            overCapacityMarkerPercent() {
                const percent = this.barMax > 0 ? (this.safeTotal / this.barMax) * 100 : 0;
                return Math.min(99.2, Math.max(0.8, percent));
            },
            activeMarkerPercent() {
                if (this.expectedToDate > 0)
                    return this.markerPercent;
                return this.hasOvertime ? this.overCapacityMarkerPercent : this.markerPercent;
            },
            activeMarkerTitle() {
                if (this.isOverCapacity)
                    return `Capacity ${this.formatLoggedHours(this.safeTotal)} hrs � ${this.formatLoggedHours(this.overCapacityHours)} hrs overtime`;
                if (this.hasToDateOvertime) {
                    const label = this.isMarkerToday ? "Today" : "Selected date";
                    return `${label} � ${this.formatLoggedHours(this.expectedToDate)} hrs expected � ${this.formatLoggedHours(this.overCapacityHours)} hrs overtime`;
                }
                return this.markerTitle;
            },
            safeEstimate() {
                return this.estimateValue == null ? 0 : (Number(this.estimateValue) || 0);
            },
            showEstimateMarker() {
                return this.safeEstimate > 0 && this.barMax > 0;
            },
            isOverEstimate() {
                return this.showEstimateMarker && this.safeTotal > this.safeEstimate;
            },
            isOverEstimateFill() {
                return this.safeEstimate > 0 && !this.isOverCapacity && this.safeValue > this.safeEstimate;
            },
            estimateOverflowHours() {
                if (!this.isOverEstimateFill)
                    return 0;
                return Math.max(0, this.safeValue - this.safeEstimate);
            },
            estimateMarkerPercent() {
                if (!this.showEstimateMarker)
                    return 0;
                const percent = (this.safeEstimate / this.barMax) * 100;
                return Math.min(99.2, Math.max(0.8, percent));
            },
            estimateMarkerTitle() {
                if (this.isOverEstimate)
                    return `Original estimate ${this.formatLoggedHours(this.safeEstimate)} hrs � ${this.formatLoggedHours(this.safeTotal - this.safeEstimate)} hrs over estimate`;
                return `Original estimate ${this.formatLoggedHours(this.safeEstimate)} hrs`;
            },
            isOnPace() {
                if (this.hasOvertime)
                    return false;
                return this.toDateHours >= this.expectedToDate && this.expectedToDate > 0;
            },
            toDateVariant() {
                if (this.toDateHours > this.expectedToDate && this.expectedToDate > 0)
                    return "success";
                return this.chooseProgressStyle(this.toDateHours, this.expectedToDate);
            },
            barVariant() {
                if (this.safeValue > this.safeTotal && this.safeTotal > 0)
                    return "danger";
                if (this.isOnPace)
                    return "success";
                return this.chooseProgressStyle(this.toDateHours, this.expectedToDate || this.safeTotal);
            },
            markerTitle() {
                const label = this.isMarkerToday ? "Today" : "Selected date";
                return `${label} � ${this.formatLoggedHours(this.expectedToDate)} hrs expected so far`;
            },
            caption() {
                if (this.safeEstimate > 0 || this.isDone)
                    return this.estimateStatusCaption;
                if (this.compact)
                    return this.progressCaption(this.safeValue, this.safeTotal);
                if (this.hasOvertime) {
                    const over = this.overCapacityHours;
                    return `${this.formatLoggedHours(over)} ${this.hourLabel(over)} overtime`;
                }
                if (this.isHoliday && this.safeTotal === 0) {
                    if (this.safeValue > 0)
                        return `${this.formatLoggedHours(this.safeValue)} ${this.hourLabel(this.safeValue)} logged`;
                    return "Holiday";
                }
                if (this.isWeekendDay && this.safeTotal === 0) {
                    if (this.safeValue > 0)
                        return `${this.formatLoggedHours(this.safeValue)} ${this.hourLabel(this.safeValue)} logged`;
                    return "Weekend";
                }
                if (this.expectedToDate > 0)
                    return this.targetCaption(this.toDateHours, this.expectedToDate);
                return this.targetCaption(this.safeValue, this.safeTotal);
            },
            estimateStatusCaption() {
                const spent = this.safeValue;
                if (spent <= 0)
                    return "";
                const reference = this.safeEstimate > 0 ? this.safeEstimate : this.safeTotal;
                if (reference > 0 && spent > reference) {
                    const over = spent - reference;
                    return `${this.formatLoggedHours(over)} ${this.hourLabel(over)} overspent`;
                }
                if (this.isDone) {
                    const spared = Math.max(0, reference - spent);
                    if (spared <= 0)
                        return "";
                    return `${this.formatLoggedHours(spared)} ${this.hourLabel(spared)} spared`;
                }
                const remaining = Math.max(0, reference - spent);
                if (remaining <= 0)
                    return "";
                return `${this.formatLoggedHours(remaining)} ${this.hourLabel(remaining)} remaining`;
            },
            isMarkerToday() {
                const marker = this.resolvedMarkerDate;
                const today = this.today;
                return marker.getFullYear() === today.getFullYear()
                    && marker.getMonth() === today.getMonth()
                    && marker.getDate() === today.getDate();
            }
        },
        methods: {
            toDate(value) {
                if (!value)
                    return null;
                if (value instanceof Date && !isNaN(value.getTime()))
                    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
                const parsed = new Date(value);
                if (isNaN(parsed.getTime()))
                    return null;
                return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
            },
            startOfWeek(date) {
                const day = date.getDay();
                const mondayOffset = day === 0 ? -6 : 1 - day;
                return new Date(date.getFullYear(), date.getMonth(), date.getDate() + mondayOffset);
            },
            maxDate(a, b) {
                return a > b ? a : b;
            },
            isWeekend(date) {
                const day = date.getDay();
                return day === 0 || day === 6;
            },
            countWorkdays(start, end) {
                if (!start || !end || end < start)
                    return 0;

                let count = 0;
                const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                while (current <= last) {
                    if (!this.isWeekend(current))
                        count += 1;
                    current.setDate(current.getDate() + 1);
                }
                return count;
            }
        }
    };
</script>

<style scoped>
    .time-progress-item + .time-progress-item {
        margin-top: 0.95rem;
    }
    .time-progress-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.75rem;
        margin-bottom: 0.3rem;
    }
    .time-progress-label {
        font-size: 0.95rem;
        font-weight: 600;
        color: #343a40;
    }
    .time-progress-hours {
        font-size: 0.82rem;
        color: #495057;
        white-space: nowrap;
    }
    .time-progress-track {
        position: relative;
    }
    .time-progress-today {
        position: absolute;
        top: -3px;
        bottom: -3px;
        width: 2px;
        background: #343a40;
        border-radius: 2px;
        box-shadow: 0 0 0 1px #fff;
        transform: translateX(-50%);
        pointer-events: none;
        z-index: 2;
    }
    .time-progress-today.is-fulfilled {
        background: #2b8a3e;
    }
    .time-progress-today.is-over {
        background: #e67700;
    }
    .time-progress-today.is-estimate {
        background: #212529;
        width: 3px;
    }
    .time-progress-today.is-estimate.is-over {
        background: #e67700;
    }
    .time-progress-track ::v-deep .overcapacity-bar,
    .time-progress-track ::v-deep .overcapacity-bar.bg-warning,
    .time-progress-track ::v-deep .progress-bar.bg-warning {
        background-color: #f59f00 !important;
    }
    .time-progress-track ::v-deep .time-progress-future-gap {
        background-color: transparent !important;
    }
    .time-progress-meta {
        margin-top: 0.25rem;
        font-size: 0.75rem;
        color: #6c757d;
    }
    .time-progress-meta.is-overtime {
        color: #d97706;
        font-weight: 600;
    }
    .time-progress-item.compact {
        width: 100%;
        min-width: 0;
    }
    .time-progress-item.compact .time-progress-head {
        margin-bottom: 0.15rem;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.25rem;
    }
    .time-progress-item.compact .time-progress-label {
        font-size: 0.75rem;
        font-weight: 700;
        white-space: nowrap;
    }
    .time-progress-item.compact .time-progress-hours {
        font-size: 0.75rem;
        font-weight: 600;
        white-space: nowrap;
    }
    .time-progress-item.compact .time-progress-meta {
        margin-top: 0.15rem;
        font-size: 0.7rem;
        white-space: nowrap;
    }
    .time-progress-item.compact + .time-progress-item.compact {
        margin-top: 0;
    }

    /* Dark Mode Support */
    [data-theme="dark"] .time-progress-label {
        color: #f1f5f9 !important;
    }
    [data-theme="dark"] .time-progress-hours {
        color: #e2e8f0 !important;
    }
    [data-theme="dark"] .time-progress-meta {
        color: #94a3b8 !important;
    }
    [data-theme="dark"] .time-progress-meta.is-overtime {
        color: #fbbf24 !important;
    }
    [data-theme="dark"] .time-progress-track ::v-deep .progress {
        background-color: #222d3d !important;
    }
    [data-theme="dark"] .time-progress-today {
        background: #e2e8f0;
        box-shadow: 0 0 0 1px #1a2332;
    }
    [data-theme="dark"] .time-progress-today.is-over {
        background: #f59f00 !important;
    }
    [data-theme="dark"] .time-progress-today.is-estimate {
        background: #f8fafc;
    }
</style>
